import { useIsMutating } from '@tanstack/react-query';

/**
 * FRONT-1996 review M7: the card and the panel each own their OWN
 * `useTakeOverOperatorRun()`/`useHandBackOperatorRun()` instance, so each
 * surface's own `mutation.isPending` only ever reflects a click THAT
 * surface made — the other surface's button stayed enabled while the
 * first one's request was still in flight, so pressing both (or the same
 * one twice from two open tabs) could genuinely send two takeover POSTs
 * for the same run. TanStack's mutation cache is shared process-wide
 * regardless of which component instance fired a mutation, so reading it
 * back via `useIsMutating` — filtered to a `mutationKey` both mutation
 * hooks declare AND a `predicate` matching THIS exact run's id off the
 * mutation's own call variables — is what lets either surface see the
 * OTHER surface's in-flight request and disable itself too.
 *
 * The mutation key constants live here (not duplicated as string literals
 * in each mutation hook) so the hooks that fire them and the hook that
 * reads them back can never drift apart.
 */
export const OPERATOR_TAKEOVER_MUTATION_KEY = ['operator-takeover'] as const;
export const OPERATOR_HAND_BACK_MUTATION_KEY = ['operator-hand-back'] as const;

function matchesRunId(runId: string | undefined) {
  return (mutation: { state: { variables?: unknown } }): boolean =>
    (mutation.state.variables as { runId?: string } | undefined)?.runId === runId;
}

export interface OperatorTakeoverMutationsInFlight {
  takingOver: boolean;
  handingBack: boolean;
}

export function useOperatorTakeoverMutationsInFlight(runId: string | undefined): OperatorTakeoverMutationsInFlight {
  const takingOverCount = useIsMutating({
    mutationKey: OPERATOR_TAKEOVER_MUTATION_KEY,
    predicate: matchesRunId(runId),
  });
  const handingBackCount = useIsMutating({
    mutationKey: OPERATOR_HAND_BACK_MUTATION_KEY,
    predicate: matchesRunId(runId),
  });
  return { takingOver: takingOverCount > 0, handingBack: handingBackCount > 0 };
}
